import React from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Dropzone, { FileRejection } from 'react-dropzone';
import { ServicesContext, useAdminService } from '~/services';
import { BSModal } from '~/components/Bootstrap';

function BulkImport() {
    const { bulkImport } = useAdminService();
    const fileProperties = {
        maxSize: 10000000,
        acceptedTypes: ['.xlsx', '.xls'],
    };
    const [show, setShow] = React.useState(false);
    const [storedFiles, setFiles] = React.useState<DropzoneFileType[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState<string[]>([]);
    const context = React.useContext(ServicesContext);

    function resetFileState() {
        setLoading(false);
        setErrors([]);
    }

    function close() {
        setShow(false);
        setFiles([]);
        setErrors([]);
        setLoading(false);
    }

    async function fileSuccess() {
        try {
            setLoading(true);
            await bulkImport(storedFiles);
            context.alert.set(
                'Upload successful. Your user import results will be emailed upon completion.',
                'success'
            );
            close();
        } catch (e) {
            if (!e?.response?.data?.violations) {
                setErrors(['An upload error occurred']);
            } else {
                setErrors(e.response.data.violations.map((x: { message: string }) => x.message));
            }
        } finally {
            setLoading(false);
        }
    }

    function fileReject(files: FileRejection[]) {
        const error: string[] = [];

        files.forEach((f) => {
            f.errors.forEach((e) => {
                if (e.code === 'file-too-large') {
                    error.push(`File is larger than 10MB (${f.file.name})`);
                } else {
                    error.push(`${e.message} (${f.file.name})`);
                }
            });
        });

        setErrors(error);
        setLoading(false);
    }

    function removeFile(file: DropzoneFileType) {
        setFiles((prev) => prev.filter((x) => x.path !== file.path));
    }

    return !context.user.isGlobalAdmin() ? null : (
        <>
            <Button variant="secondary" onClick={() => setShow(true)} className="me-2">
                Bulk Import
            </Button>
            <BSModal
                show={show}
                onHide={close}
                aria-labelledby="bulk-import-title"
                id="bulk-import-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="bulk-import-title" role="heading" aria-level={1}>
                        Bulk User Import
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-end mb-3">
                        <a href="/resources/help/zephyr-user-bulk-load-sample.xlsx" target="_blank">
                            Download Import Template
                        </a>
                    </div>
                    <Dropzone
                        accept={fileProperties.acceptedTypes}
                        maxSize={fileProperties.maxSize}
                        onDrop={resetFileState}
                        onDropAccepted={(files) => setFiles(files as DropzoneFileType[])}
                        onDropRejected={fileReject}
                        multiple={false}
                        disabled={loading}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <div {...getRootProps({ className: 'dropzone mb-3' })}>
                                <input {...getInputProps()} aria-label="User Import File" />
                                Drop files to attach, or click to browse
                            </div>
                        )}
                    </Dropzone>
                    {errors.length > 0 && (
                        <div className="mb-2">
                            {errors.map((e) => (
                                <div key={e} className="text-danger fw-bold">
                                    {e}
                                </div>
                            ))}
                        </div>
                    )}

                    {storedFiles.map((file) => (
                        <div className="mb-1 file-list" key={file.path}>
                            <i
                                className="fa-solid fa-times-circle text-danger me-2"
                                onClick={() => removeFile(file)}
                                onKeyPress={() => removeFile(file)}
                                role="button"
                                tabIndex={0}
                                aria-label="Remove File"
                            />
                            {file.path}
                        </div>
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={close}>
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        onClick={fileSuccess}
                        disabled={loading || storedFiles.length === 0}
                    >
                        {loading && (
                            <Spinner animation="border" size="sm" role="status" className="me-2" />
                        )}
                        Submit
                    </Button>
                </Modal.Footer>
            </BSModal>
        </>
    );
}

export { BulkImport };
